import type { ReactElement } from "react"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import ResponsiveImage from "@gatsby-plugin-generic-page/components/ResponsiveImage"
import type {
  BenefitCardFlatData,
  BenefitCardIcon,
} from "@gatsby-plugin-generic-page/fragments/benefitCard"
import { BG_CLASS } from "@gatsby-plugin-generic-page/fragments/udsBackgroundColor"
import { Text } from "@ionos-web-design-system/react"
import DynamicUdsIcon, {
  ICON_GROUP_SQUIDEX_TYPE,
} from "~/components/DynamicUdsIcon"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"
import type { ResponsiveImages } from "~/types/squidex"

function isUdsIcon(
  icon: BenefitCardIcon
): icon is Exclude<BenefitCardIcon, ResponsiveImages & { __typename: string }> {
  return "iconId" in icon
}

function isImageIcon(
  icon: BenefitCardIcon
): icon is ResponsiveImages & { __typename: string } {
  return "large" in icon
}

type BenefitCardProps = BenefitCardFlatData & { className?: string }

export default function BenefitCard({
  icon,
  title,
  text,
  image,
  backgroundColor,
  border,
  borderRadius,
  shadow,
  className,
}: BenefitCardProps): ReactElement<BenefitCardProps> {
  const bgColorClass =
    !isEmpty(backgroundColor) &&
    backgroundColor.color &&
    BG_CLASS[backgroundColor.color]
  const borderClass =
    !isEmpty(border) &&
    border.type !== "none" &&
    `border border--${border.type}`
  const borderRadiusClass =
    !isEmpty(borderRadius) &&
    borderRadius.size !== "none" &&
    `rounded-${borderRadius.size}`
  const shadowClass =
    !isEmpty(shadow) && shadow.size !== "none" && `shadow-${shadow.size}`

  const cardClassName = cn(
    "p-4 md:p-5",
    bgColorClass,
    borderClass,
    borderRadiusClass,
    shadowClass,
    className
  )

  return (
    <article className={cardClassName}>
      {!isEmpty(icon) && isUdsIcon(icon) ? (
        <div className="mb-4" data-testid="uds-icon">
          <DynamicUdsIcon
            iconName={icon.iconId}
            iconGroup={ICON_GROUP_SQUIDEX_TYPE[icon.__typename]}
          />
        </div>
      ) : null}

      {!isEmpty(icon) && isImageIcon(icon) ? (
        <div className="mb-4 w-16 h-16 [&_img]:w-full [&_img]:h-full [&_img]:object-contain">
          <ResponsiveImage images={icon} />
        </div>
      ) : null}

      <Text variant="heading2xl" weight="bold" color="base" asChild>
        <h3>{title}</h3>
      </Text>

      {!isEmpty(text) ? (
        <div className="mt-3">
          <MarkdownText>{text}</MarkdownText>
        </div>
      ) : null}

      {!isEmpty(image) ? (
        <div className="mt-4 [&_img]:w-full">
          <ResponsiveImage images={image} />
        </div>
      ) : null}
    </article>
  )
}
